import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { Laboratory } from '../_models/laboratory';
import { ReleaseNote } from '../_models/release-note';
import { AuthenticationService } from '../_services/authentication.service';
import { environment } from 'src/environments/environment';
import {
  ReleaseNoteCategory,
  ReleaseNoteCategoryDisplay,
} from '../_models/release-note-categories';
import { ReleaseNoteComponent } from '../_components/cards/release-note/release-note.component';
import { IconButtonComponent } from '../_components/buttons/icon-button/icon-button.component';
import { FilterModalComponent } from '../_components/modals/filter-modal/filter-modal.component';
import { ModalService } from '../_services/modal.service';
import { FormArray, FormBuilder } from '@angular/forms';
import { FormBase } from '../_models/form-base';
import { clearFormArray } from '../_helpers/utils';
import { RELEASE_NOTES } from './release-notes.data';

@Component({
  selector: 'app-about',
  imports: [ReleaseNoteComponent, IconButtonComponent, FilterModalComponent],
  templateUrl: './about.component.html',
  styleUrl: './about.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AboutComponent implements OnInit {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly modalService = inject(ModalService);
  private readonly formBuilder = inject(FormBuilder);

  laboratory = signal<Laboratory | undefined>(undefined);
  appVersion = environment.appVersion;

  readonly isProcessing = signal(false);
  readonly selectedCategoriesList = signal<ReleaseNoteCategory[]>([]);

  readonly releaseNotes: ReleaseNote[] = [...RELEASE_NOTES];
  readonly filteredReleaseNotes = signal<ReleaseNote[]>([]);

  categoryFilters = new FormBase({
    key: 'category',
    label: 'Filter by category:',
    type: 'checkbox',
    placeholder: '',
    disabled: false,
    required: false,
    value: null,
    options: [
      {
        key: ReleaseNoteCategory.NewFeature,
        value: ReleaseNoteCategoryDisplay[ReleaseNoteCategory.NewFeature],
      },
      {
        key: ReleaseNoteCategory.Improvements,
        value: ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Improvements],
      },
      {
        key: ReleaseNoteCategory.API,
        value: ReleaseNoteCategoryDisplay[ReleaseNoteCategory.API],
      },
      {
        key: ReleaseNoteCategory.Device,
        value: ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Device],
      },
      {
        key: ReleaseNoteCategory.Security,
        value: ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Security],
      },
      {
        key: ReleaseNoteCategory.WebApp,
        value: ReleaseNoteCategoryDisplay[ReleaseNoteCategory.WebApp],
      },
      {
        key: ReleaseNoteCategory.Performance,
        value: ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Performance],
      },
      {
        key: ReleaseNoteCategory.BugFixes,
        value: ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes],
      },
      {
        key: ReleaseNoteCategory.UX,
        value: ReleaseNoteCategoryDisplay[ReleaseNoteCategory.UX],
      },
    ],
  });

  filterControls = this.formBuilder.group({
    [this.categoryFilters.key]: new FormArray([]),
  });

  ngOnInit(): void {
    this.filteredReleaseNotes.set(this.releaseNotes);
    this.laboratory.set(this.authenticationService.selectedLaboratory);
    this.initialiseCheckboxes();
  }

  get checkboxesFormArray(): FormArray {
    return this.filterControls.get('category') as FormArray;
  }

  initialiseCheckboxes(): void {
    clearFormArray(this.checkboxesFormArray);
    this.categoryFilters.options.forEach(() => {
      const control = this.formBuilder.control(false);
      this.checkboxesFormArray.push(control);
    });
  }

  moduleCheckboxClicked(event: Event): void {
    const target = event.target as HTMLInputElement;
    const category = parseInt(target.name) as ReleaseNoteCategory;
    if (target.checked) {
      this.selectedCategoriesList.update((list) => [...list, category]);
    } else {
      this.selectedCategoriesList.update((list) => list.filter((x) => x !== category));
    }
  }

  closeModal(modal: string): void {
    this.modalService.close(modal);
  }

  openModal(modal: string): void {
    this.modalService.open(modal);
  }

  resetFilters(): void {
    const resetValues = this.checkboxesFormArray.controls.map(() => false);
    this.checkboxesFormArray.patchValue(resetValues);
    this.filterControls.markAsPristine();
    this.filterControls.markAsUntouched();
    this.filteredReleaseNotes.set(this.releaseNotes);
    this.selectedCategoriesList.set([]);
  }

  filterModalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'reset-button':
        this.resetFilters();
        break;
      case 'continue-button':
        this.filteredReleaseNotes.set(
          this.releaseNotes.filter((note) =>
            note.categories.some((category) =>
              this.selectedCategoriesList().includes(category),
            ),
          ),
        );
        this.closeModal('filter');
        break;
    }
  }
}
